import * as ApiClient from '@/libs/ApiClient';
import type { AxiosResponse } from 'axios';
import type { GeneralResp } from '@/models/GeneralResp';
import { RouteUrlList } from '@/libs/RouteList';

/**
 * 業務分類 DTO
 */
export interface IBusinessCategoryDTO {
    /** 分類唯一識別碼 */
    categoryID: number;
    /** 父分類ID */
    parentID: number | null;
    /** 分類代碼 */
    categoryAlias: string;
    /** 分類名稱 */
    categoryName: string;
    /** 分類層級 (1:申請業務, 2:業種, 3:類別) */
    categoryLevel: number;
    /** 排序順序 */
    sortOrder: number;
    /** 是否啟用 Y/N */
    isActive: boolean;
    /** 是否刪除 Y/N */
    isDeleted: boolean;
    /** 建立時間 */
    createdTime: string | null;
    /** 更新時間 */
    lastUpdate: string | null;
    /** 說明 */
    description: string | null;
}

/**
 * 上傳項目 DTO
 */
export interface IUploadItemDTO {
    /** 項目ID */
    itemID: number;
    /** 父項目ID */
    parentID: number | null;
    /** 父項目名稱 */
    parentItemName: string | null;
    /** 項目層級 */
    itemLevel: number;
    /** 項目名稱 */
    itemName: string;
    /** 是否為葉節點 */
    isLeafNode: boolean;
    /** 排序順序 */
    sortOrder: number;
    /** 是否啟用 */
    isActive: boolean;
    /** 建立時間 */
    createdTime: string | null;
    /** 更新時間 */
    lastUpdate: string | null;
    /** 說明 */
    description: string | null;
    /** 支援的檔案格式 */
    fileFormat: string | null;
    /** 最大檔案大小 (MB) */
    maxFileSize: number | null;
    /** 最多上傳筆數 */
    maxMultiUpload: number | 1;
    /** 總筆數 */
    totalCount: number;
}

/**
 * 上傳項目階層 DTO
 */
export interface IUploadItemHierarchyDTO extends IUploadItemDTO {
    /** 子項目集合 */
    children: IUploadItemHierarchyDTO[];
}

/**
 * 案件上傳項目選擇 DTO
 */
export interface ICaseUploadSelectionDTO {
    selectionID: number;
    caseID: number;
    uploadItemID: number;
    /** 項目ID（用於關聯上傳項目） */
    itemID: number;
    isSelected: string;
    createdTime: string | null;
    lastUpdate: string | null;
}

/**
 * 已上傳文件 DTO
 */
export interface IUploadedDocumentsDTO {
    documentID: number;
    caseID: number;
    uploadItemID: number;
    fileName: string;
    filePath: string;
    fileSize: number;
    uploadTime: string;
    uploadedBy: string;
    status: string;
}

/**
 * 案件基本資料 DTO
 */
export interface IDocumentCaseDTO {
    /** 申請業務類別ID（用於關聯業務分類） */
    businessCategoryID: number;
}

export enum SupplementStatus {
    /// <summary>
    /// (0) 無補件項目
    /// </summary>
    None = 0,

    /// <summary>
    /// (1) 有進行中案件
    /// </summary>
    InProgress = 1,

    /// <summary>
    /// (2) 全部完成
    /// </summary>
    Completed = 2
}
/**
 * 補件案件完整資訊
 */
export interface IDocCaseClientFullInfo {
    /** 案件基本資料 */
    caseData: IDocumentCaseDTO;
    /** 案件上傳項目選擇列表 */
    caseUploadSelections: ICaseUploadSelectionDTO[] | null;
    /** 已上傳文件列表 */
    uploadedDocuments: IUploadedDocumentsDTO[] | null;
}

/**
 * 業務選擇頁面資料回應
 */
export interface IViewDataResponse {
    /** 回應狀態 */
    status: boolean;
    /** 錯誤代碼 */
    code: number;
    /** 例外訊息 */
    exception: string | null;
    /** 訊息 */
    message: string | null;
    /** 請選擇欲補件業務 */
    businessCategoryList: IBusinessCategoryDTO[] | null;
    /** 補件的項目 巢狀結構 */
    uploadItems: IUploadItemHierarchyDTO[] | null;
    /** 補件案件所有資訊（用於編輯，顧客端顯示用） */
    clientCaseInfo: IDocCaseClientFullInfo[] | null;
    /** 補件狀態 0:無補件項目; 1:有進行中案件; 2:全部完成 */
    caseSupplementStatus: SupplementStatus;
    /** 登入分公司名稱（當無補件項目時回傳） */
    branchName: string | null;
    /** 登入分公司電話（當無補件項目時回傳） */
    branchTel: string | null;
    /** 個人資料告知事項 HTML 內容 */
    contractHtml: string | null;
}

/**
 * 業務選擇 - 取得頁面資料 API
 *
 * @description 取得業務分類列表和上傳項目階層結構
 * @returns Promise<IViewDataResponse>
 */
const BusinessSelectionViewData = (): Promise<IViewDataResponse> => {
    const url = RouteUrlList.API_BusinessSelectionViewData.path;
    const config = {
        params: {
            showOverlay: ApiClient.paramTypeEnum.true,
            getServiceAlert: ApiClient.paramTypeEnum.true
        }
    };

    return ApiClient.post(url, {}, config).then((res: AxiosResponse<IViewDataResponse>) => {
        return res.data;
    });
};

export default BusinessSelectionViewData;
